import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  Add16Regular,
  ArrowUndoRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  CopyRegular,
  Dismiss12Regular,
  DismissRegular,
} from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTreeStore, type Tab } from '@/stores/treeStore';
import { useUiStore } from '@/stores/uiStore';
import { useContainers } from '@/hooks/useContainers';
import { computeTabLabel, computeTabPath } from './tabLabel';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    padding: `0 ${tokens.spacingHorizontalXS}`,
    height: '32px',
    flex: 1,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    '::-webkit-scrollbar': { display: 'none' },
  },
  scrollBtn: {
    flexShrink: 0,
    minWidth: 0,
    width: '22px',
    height: '24px',
    padding: 0,
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    // Ancre du bouton de fermeture, placé en ABSOLU (cf. closeBtn).
    position: 'relative',
    // Padding SYMÉTRIQUE : l'onglet n'est plus asymétrique pour ménager une place
    // au bouton de fermeture.
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    height: '24px',
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusSmall,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
    maxWidth: '160px',
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  // Onglet ACTIF : aplat de marque, pas la teinte `colorBrandBackground2`
  // d'avant. Cette teinte est quasi indiscernable du fond de la barre —
  // #082338 contre #1f1f1f en thème sombre, deux luminances égales à 0,001 près :
  // seul le survol (#0c3b5e) rendait l'onglet visible, si bien que l'onglet actif
  // était moins lisible que celui qu'on effleurait. L'aplat #115ea3 / #0f6cbd
  // tranche dans les deux thèmes, et le texte blanc y garde un contraste de 5,9:1
  // (sombre) et 4,9:1 (clair), au-dessus du seuil AA.
  tabActive: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontWeight: tokens.fontWeightSemibold,
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover },
  },
  tabDragging: { opacity: 0.5 },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    // `minWidth: 0` est INDISPENSABLE : un enfant flex refuse par défaut de
    // descendre sous sa taille de contenu, et sans cela le texte déborderait au
    // lieu de s'abréger.
    minWidth: 0,
  },
  // Titre RACCOURCI pendant que la croix est visible : le texte s'estompe sur ses
  // 18 derniers pixels, là où la croix se superpose.
  //
  // Un masque, et non une largeur réduite : toucher à la largeur du libellé
  // rétrécirait l'onglet, dont la taille dépend de son contenu — or l'onglet ne
  // doit pas bouger. La boîte du libellé reste donc intacte, seul son rendu est
  // rogné. C'est aussi ce que fait Chrome, qui estompe les titres d'onglet au lieu
  // de les couper par des points de suspension.
  labelShort: {
    maskImage: 'linear-gradient(to right, #000 calc(100% - 18px), transparent)',
    WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 18px), transparent)',
  },
  closeBtn: {
    // EN ABSOLU, donc HORS FLUX : le bouton n'occupe aucune place dans la mise en
    // page, si bien que son apparition au survol ne peut pas élargir l'onglet — et
    // qu'aucune zone vide n'est réservée quand il est masqué. C'est le titre qui
    // cède la place, en s'estompant sous le bouton (cf. labelShort).
    position: 'absolute',
    right: '2px',
    top: '50%',
    transform: 'translateY(-50%)',
    minWidth: 0,
    padding: 0,
    width: '16px',
    height: '16px',
    flexShrink: 0,
    // La croix reprend la couleur de l'onglet : sur l'onglet actif (aplat de
    // marque) le gris `colorNeutralForeground2` du bouton subtil passerait mal, et
    // en thème clair son fond de survol presque blanc effacerait une croix
    // blanche. On neutralise donc les deux, le survol de l'onglet entier servant
    // déjà d'indication.
    color: 'inherit',
    backgroundColor: 'transparent',
    ':hover': { color: 'inherit', backgroundColor: 'transparent' },
    ':hover:active': { color: 'inherit', backgroundColor: 'transparent' },
  },
  // `display: none` est ici sans conséquence sur la largeur (le bouton est hors
  // flux) et il a l'avantage de le retirer aussi de l'arbre d'accessibilité.
  closeHidden: { display: 'none' },
  addBtn: {
    flexShrink: 0,
    marginLeft: tokens.spacingHorizontalXXS,
    minWidth: 0,
    padding: 0,
    width: '20px',
    height: '20px',
  },
});

interface TabItemProps {
  tab: Tab;
  isActive: boolean;
}

function TabItem({ tab, isActive }: TabItemProps) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  // Subscribe to the containers query so the label re-renders when it changes
  // (e.g. after an admin renames a space, which invalidates ['containers']).
  // computeTabLabel reads the cache directly, so we just need the re-render.
  useContainers();
  const activateTab = useTreeStore((s) => s.activateTab);
  const closeTab = useTreeStore((s) => s.closeTab);
  const duplicateTab = useTreeStore((s) => s.duplicateTab);
  const reopenLastClosed = useTreeStore((s) => s.reopenLastClosed);
  const recentlyClosed = useTreeStore((s) => s.recentlyClosed);
  const [hover, setHover] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  // Recherche PROPRE À CET ONGLET : quand elle est active, c'est elle qui donne
  // son libellé — l'onglet montre des résultats, pas le projet dont il porte le
  // nom.
  const tabSearch = useUiStore((s) => s.searchByTab[tab.id] ?? '');
  const label = computeTabLabel(tab, queryClient, tabSearch);
  const path = computeTabPath(tab, queryClient, tabSearch);

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <Menu openOnContext>
      <MenuTrigger disableButtonEnhancement>
        <div
          ref={setNodeRef}
          data-tab-id={tab.id}
          {...attributes}
          {...listeners}
          style={style}
          className={mergeClasses(
            styles.tab,
            isActive && styles.tabActive,
            isDragging && styles.tabDragging,
          )}
          onClick={() => activateTab(tab.id)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onAuxClick={(e) => {
            if (e.button === 1) {
              e.preventDefault();
              closeTab(tab.id);
            }
          }}
          title={path}
        >
          <span className={mergeClasses(styles.label, hover && styles.labelShort)}>{label}</span>
          <Button
            appearance="subtle"
            size="small"
            className={mergeClasses(styles.closeBtn, !hover && styles.closeHidden)}
            icon={<Dismiss12Regular />}
            title="Fermer l'onglet"
            onClick={(e) => {
              e.stopPropagation();
              closeTab(tab.id);
            }}
          />
        </div>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem icon={<DismissRegular />} onClick={() => closeTab(tab.id)}>
            Fermer l'onglet
          </MenuItem>
          <MenuItem
            icon={<ArrowUndoRegular />}
            disabled={recentlyClosed.length === 0}
            onClick={() => reopenLastClosed()}
          >
            Rouvrir le dernier onglet fermé
          </MenuItem>
          <MenuItem icon={<CopyRegular />} onClick={() => duplicateTab(tab.id)}>
            Dupliquer l'onglet
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function useOverflow(barRef: React.RefObject<HTMLDivElement | null>) {
  const [state, setState] = useState({ canLeft: false, canRight: false });

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const update = () => {
      const canLeft = el.scrollLeft > 0;
      const canRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setState((prev) =>
        prev.canLeft === canLeft && prev.canRight === canRight
          ? prev
          : { canLeft, canRight },
      );
    };
    update();
    el.addEventListener('scroll', update);
    const ro = new ResizeObserver(update);
    ro.observe(el);
    // Also watch children to react to tab add/remove.
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [barRef]);

  return state;
}

export function TabBar() {
  const styles = useStyles();
  const tabs = useTreeStore((s) => s.tabs);
  const activeTabId = useTreeStore((s) => s.activeTabId);
  const openTab = useTreeStore((s) => s.openTab);
  const moveTab = useTreeStore((s) => s.moveTab);
  const barRef = useRef<HTMLDivElement | null>(null);
  const { canLeft, canRight } = useOverflow(barRef);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // Scroll the active tab into view when it changes (e.g. via shortcut or
  // programmatic activation).
  useEffect(() => {
    const el = barRef.current?.querySelector<HTMLElement>(
      `[data-tab-id="${activeTabId}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [activeTabId]);

  if (tabs.length < 2) return null;

  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over) return;
    moveTab(String(e.active.id), String(e.over.id));
  };

  const scrollBy = (delta: number) => {
    barRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className={styles.wrapper}>
      {/* Scroll arrows appear only when the tabs actually overflow in that
          direction — no more permanently-greyed left arrow when everything
          already fits. */}
      {canLeft && (
        <Button
          appearance="subtle"
          size="small"
          className={styles.scrollBtn}
          icon={<ChevronLeftRegular />}
          title="Défiler à gauche"
          onClick={() => scrollBy(-120)}
        />
      )}
      <div className={styles.bar} ref={barRef}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={tabs.map((t) => t.id)} strategy={horizontalListSortingStrategy}>
            {tabs.map((tab) => (
              <TabItem key={tab.id} tab={tab} isActive={tab.id === activeTabId} />
            ))}
          </SortableContext>
        </DndContext>
        <Button
          appearance="subtle"
          size="small"
          className={styles.addBtn}
          icon={<Add16Regular />}
          title="Nouvel onglet"
          onClick={() => openTab({ containerId: null, folderId: null, activate: true })}
        />
      </div>
      {canRight && (
        <Button
          appearance="subtle"
          size="small"
          className={styles.scrollBtn}
          icon={<ChevronRightRegular />}
          title="Défiler à droite"
          onClick={() => scrollBy(120)}
        />
      )}
    </div>
  );
}
